import { useCallback, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useSweetpassSubscribe } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { type useSweetpassMembershipData } from '../useSweetpassMembershipData';

export function useSweetpassSubscriptionSwitch(
  props: UseSweetpassSubscriptionSwitchProps,
) {
  const {
    subscriptionPlanId = '',
    activePaymentMethodId = '',
    nextBillingDate = '',
    hasPendingSubscription,
    isSubscriptionAnnual,
    planIdAfterSwitch,
    refetchMembership,
  } = props;

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();

  const { subscribe: switchSubscriptionBack, isSubscribing: isSwitchingBack } =
    useSweetpassSubscribe();

  // ─── State ───────────────────────────────────────────────────────────

  const [isChangingSubscription, setIsChangingSubscription] = useState(false);
  const planIdToBeSwitchedTo = hasPendingSubscription
    ? planIdAfterSwitch
    : subscriptionPlanId;

  // ─── Switch Subscription Back ────────────────────────────────────────

  const handleSwitchSubscriptionBack = useCallback(async () => {
    const { isSuccess } = await switchSubscriptionBack({
      planId: subscriptionPlanId,
      paymentId: activePaymentMethodId,
    });

    if (isSuccess) {
      const annual = t('account.sweetpass-membership.membership-level.annual');

      const monthly = t(
        'account.sweetpass-membership.membership-level.monthly',
      );

      const currentPlanRecurrence = isSubscriptionAnnual ? annual : monthly;
      const requestedPlanRecurrence = isSubscriptionAnnual ? monthly : annual;

      addNoticeBanner({
        text: t('account.sweetpass-membership.membership-level.undo-success', {
          currentPlanRecurrence,
          requestedPlanRecurrence,
        }),
        palette: 'success',
      });

      refetchMembership();

      return;
    }

    addNoticeBanner({
      text: t('account.sweetpass-membership.membership-level.change-error'),
      palette: 'caution',
    });
  }, [
    subscriptionPlanId,
    activePaymentMethodId,
    isSubscriptionAnnual,
    t,
    switchSubscriptionBack,
    addNoticeBanner,
    refetchMembership,
  ]);

  // ─── Switch Subscription ─────────────────────────────────────────────

  const switchSubscriptionHandler = useCallback(async () => {
    track('sweetpass_account.change_cta.tapped');

    if (hasPendingSubscription) {
      await handleSwitchSubscriptionBack();

      return;
    }

    setIsChangingSubscription(true);
  }, [hasPendingSubscription, track, handleSwitchSubscriptionBack]);

  const closeChangingSubscriptionModal = useCallback(() => {
    setIsChangingSubscription(false);
  }, []);

  // ─── Subscription Switch Success ─────────────────────────────────────

  const handleSubscriptionSwitchSuccess = useCallback(() => {
    const isChangingToMonthly =
      (isSubscriptionAnnual && !hasPendingSubscription) ||
      (!isSubscriptionAnnual && hasPendingSubscription);

    const requestedPlanRecurrence = isChangingToMonthly
      ? t('account.sweetpass-membership.membership-level.monthly')
      : t('account.sweetpass-membership.membership-level.annual');

    const switchDate = format(parseISO(nextBillingDate), 'MM/dd/yyyy');

    addNoticeBanner({
      text: t('account.sweetpass-membership.membership-level.change-success', {
        switchDate,
        requestedPlanRecurrence,
      }),
      palette: 'success',
    });

    refetchMembership();
  }, [
    isSubscriptionAnnual,
    hasPendingSubscription,
    nextBillingDate,
    t,
    addNoticeBanner,
    refetchMembership,
  ]);

  return {
    isSwitchingBack,
    isChangingSubscription,
    planIdToBeSwitchedTo,
    switchSubscriptionHandler,
    closeChangingSubscriptionModal,
    handleSubscriptionSwitchSuccess,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassSubscriptionSwitchProps = Omit<
  ReturnType<typeof useSweetpassMembershipData>,
  'loyaltyProfileError' | 'hasMembershipError' | 'isSweetpassMembershipLoading'
>;
